// Library methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ThemeProvider, createTheme, useMediaQuery } from "@mui/material";

// MUI Components
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";

// Assets
import eyeLogo from "../../../../../assets/images/logos/eyelogo.png";

// Utilities
import { ValidatePDFContext } from "../../../../../contexts/ValidatePDFContext";
import { ReportDataContext } from "../../../../../contexts/ReportDataContext";
import ChartLegendExceptVisualField from "../../../../../components/UI/ChartLegendExceptVisualField";
import {
  ReportInfoHeaderContent,
  ReportInfoShared,
} from "../../../../../components/UI/ReportInfoShared";
import { ReportEachSectionContext } from "../../../../../contexts/CommentContext";
import { examCardEnums, eyeTypes } from "../../../../../enums";
import useClinic from "../../../../../hooks/useClinic";
import { getLocalDateTimeWithZone } from "../../../../../utils/dateHelper";
import { getTutorialCompletionText } from "../../../../../utils/examReportsHelper";

const ReportInfo = () => {
  // internationalization
  const { t } = useTranslation();

  const { clinicTimezone } = useClinic();
  const { forBackendPdf } = useContext(ValidatePDFContext);
  const { exam } = useContext(ReportDataContext);
  const { examReport, eyeReport, duration } = useContext(
    ReportEachSectionContext
  );
  const { eyeRight, eyeBoth } = eyeTypes;

  const theme = createTheme({
    typography: {
      allVariants: {
        fontFamily: ["Arial", "Verdana", "sans-serif"],
      },
    },
  });
  const isWiderThanSmallScreen = useMediaQuery(theme.breakpoints.up("sm"));

  const localDateAndTimeWithZone = useMemo(
    () =>
      examReport?.timing?.start
        ? getLocalDateTimeWithZone(examReport.timing.start, clinicTimezone)
        : null,
    [clinicTimezone, examReport?.timing?.start]
  );

  const eyeAbbreviation = useMemo(
    () => (eyeReport === eyeBoth ? "OU" : eyeReport === eyeRight ? "OD" : "OS"),
    [eyeReport, eyeBoth, eyeRight]
  );
  const gridSizeData = useMemo(
    () => [
      { sm: 4, md: 4, lg: 4, xl: 4 },
      {
        sm: 3,
        md: 3,
        lg: 3,
        xl: 2.4,
      },
    ],
    []
  );

  const isBaileyLovie = useMemo(
    () => examReport?.exam?.algorithm === "BAILEY_LOVIE",
    [examReport?.exam?.algorithm]
  );

  const visualAcuityScore = useMemo(() => {
    if (isBaileyLovie) {
      let scoreString = "";
      examReport?.postProcessing?.processedCharts?.forEach((processedChart) => {
        if (!scoreString.length) {
          scoreString += processedChart?.result?.split(" ")?.[0];
        } else {
          scoreString += ` / ${processedChart?.result?.split(" ")?.[0]}`;
        }
      });
      return scoreString;
    }
    return examReport?.postProcessing?.processedCharts?.[0]?.result;
  }, [isBaileyLovie, examReport?.postProcessing]);

  const infoData = useMemo(
    () => [
      [
        {
          title: t("word_eye"),
          body:
            eyeReport === eyeRight
              ? t("word_right")
              : eyeReport === eyeBoth
                ? t("word_binocular")
                : t("word_left"),
        },
        { title: t("score"), body: visualAcuityScore },
        {
          title: t("exams_table_position"),
          body: exam?.visualAcuitySections?.[0]?.position,
        },
      ],
      [
        {
          title: t("report_exam_date"),
          body: localDateAndTimeWithZone?.date ?? t("word_na"),
          isBlack: true,
        },
        {
          title: t("report_exam_time"),
          body: localDateAndTimeWithZone?.time ?? t("word_na"),
          isBlack: true,
        },
        { title: t("report_exam_duration"), body: duration },
        getTutorialCompletionText(
          examReport?.postProcessing?.tutorialCompletionState,
          t
        ) && {
          title: t("word_tutorial"),
          body: getTutorialCompletionText(
            examReport?.postProcessing?.tutorialCompletionState,
            t
          ),
          isBlack: true,
        },
      ],
    ],
    [
      visualAcuityScore,
      duration,
      exam?.visualAcuitySections,
      eyeReport,
      localDateAndTimeWithZone?.date,
      localDateAndTimeWithZone?.time,
      examReport?.postProcessing?.tutorialCompletionState,
      eyeRight,
      eyeBoth,
      t,
    ]
  );

  return (
    <ThemeProvider theme={theme}>
      <Grid
        container
        columnSpacing={2}
        my={1}
        sx={{ display: "flex", alignItems: "start" }}
      >
        <Grid container item xs={12}>
          {forBackendPdf && (
            <Grid
              item
              xs={1}
              style={{
                alignItems: "center",
              }}
            >
              <img
                src={eyeLogo}
                alt="eye logo"
                style={{ width: "100%", height: "auto" }}
              />
            </Grid>
          )}
          <Grid container item xs={forBackendPdf ? 11 : 12} mb={1.5}>
            <Grid
              item
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              border={1}
              xs={12}
            >
              <Grid
                item
                sx={{
                  display: "flex",
                  background: "black",
                  width: 70,
                  height: "100%",
                  color: "white",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                xs={1.5}
                sm={1}
              >
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {eyeAbbreviation}
                </Typography>
              </Grid>
              <ReportInfoHeaderContent
                isWiderThanSmallScreen={isWiderThanSmallScreen}
                examTypeTranslation={`${t(examCardEnums.VISUAL_ACUITY.label)} - `}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container item display="flex" justifyContent={"space-between"}>
          <ReportInfoShared data={infoData} gridSizeData={gridSizeData} />
        </Grid>
        <Grid container item mt={2}>
          <ChartLegendExceptVisualField />
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default ReportInfo;
